import { useMemo } from 'react';
import { getGamesByCategory, getLatestGames, type Game } from '@lib/content';
import { GAME_LIMITS } from '../config';

export interface GameRecommendations {
  sidebarGames: Game[];
  moreGames: Game[];
}

/**
 * Get series/version games for current game
 */
function getSeriesGames(currentGame: Game, allCategoryGames: Game[]): Game[] {
  const currentTitle = currentGame.title.toLowerCase();
  const currentSlug = currentGame.slug.toLowerCase();

  // Extract base name from title (remove numbers and common words)
  const getBaseName = (title: string) => {
    return title
      .toLowerCase()
      .replace(/\b(1|2|3|4|5|6|7|8|9|10|i+|ii+|iii+|iv+|v+)\b/g, '')
      .replace(/\b(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)\b/g, '')
      .replace(/[-:\s]+/g, ' ')
      .trim()
      .replace(/\s+/g, ' ');
  };

  const getBaseSlug = (slug: string) => {
    return slug
      .replace(/-(\d+)$/, '') // Remove trailing numbers
      .replace(/-(epic|battle|new|super|mega|ultra|pro|plus|deluxe|winter|spooky|pool|party)(-\d+)?$/, '')
      .replace(/-(1|2|3|4|5|6|7|8|9|10)(-\w+)*$/, '');
  };

  const currentBaseName = getBaseName(currentGame.title);
  const currentBaseSlug = getBaseSlug(currentGame.slug);

  // Find series games
  const seriesGames = allCategoryGames.filter(game => {
    if (game.slug === currentGame.slug) return false;

    const gameBaseName = getBaseName(game.title);
    const gameBaseSlug = getBaseSlug(game.slug);

    // Check if same series by title or slug
    return (currentBaseName === gameBaseName && currentBaseName.length > 3) ||
      (currentBaseSlug === gameBaseSlug && currentBaseSlug.length > 3) ||
      // Manual series mappings for common cases
      isInSameSeries(currentGame.slug, game.slug);
  });

  // Sort by version/number (try to get sequential order)
  return seriesGames.sort((a, b) => {
    const aNum = extractNumber(a.title) || extractNumber(a.slug) || 0;
    const bNum = extractNumber(b.title) || extractNumber(b.slug) || 0;
    return aNum - bNum;
  });
}

/**
 * Extract number from title/slug for version ordering
 */
function extractNumber(text: string): number | null {
  const match = text.match(/\b(\d+)\b/);
  return match ? parseInt(match[1], 10) : null;
}

/**
 * Manual series detection for common game series (based on actual games in project)
 */
function isInSameSeries(slug1: string, slug2: string): boolean {
  const seriesMap: Record<string, string[]> = {
    // American Football / Rugby
    'american-football-kick': ['4th-and-goal-2022', '2-minute-football', 'retro-bowl-college-football', 'touchdowners', 'rugby-rush'],
    '4th-and-goal-2022': ['american-football-kick', '2-minute-football', 'retro-bowl-college-football', 'touchdowners', 'rugby-rush'],
    '2-minute-football': ['american-football-kick', '4th-and-goal-2022', 'retro-bowl-college-football', 'touchdowners', 'rugby-rush'],
    'retro-bowl-college-football': ['american-football-kick', '4th-and-goal-2022', '2-minute-football', 'touchdowners', 'rugby-rush'],
    'touchdowners': ['american-football-kick', '4th-and-goal-2022', '2-minute-football', 'retro-bowl-college-football', 'rugby-rush'],
    'rugby-rush': ['american-football-kick', '4th-and-goal-2022', '2-minute-football', 'retro-bowl-college-football', 'touchdowners'],

    // Football / Soccer Main Series
    'football-legends-2': ['football-legends', 'football-masters', 'football-stars', 'soccer-bros', 'soccer-legends'],
    'football-legends': ['football-legends-2', 'football-masters', 'football-stars', 'soccer-bros', 'soccer-legends'],
    'football-masters': ['football-legends-2', 'football-legends', 'football-stars', 'soccer-bros', 'soccer-legends'],
    'football-stars': ['football-legends-2', 'football-legends', 'football-masters', 'soccer-bros', 'soccer-legends'],
    'soccer-bros': ['football-legends-2', 'football-legends', 'football-masters', 'football-stars', 'soccer-legends'],
    'soccer-legends': ['football-legends-2', 'football-legends', 'football-masters', 'football-stars', 'soccer-bros'],

    // Football / Soccer Action
    'football-brawl': ['football-blitz', 'football-killer', 'football-strike', 'head-soccer', 'head-soccer-2-player'],
    'football-blitz': ['football-brawl', 'football-killer', 'football-strike', 'head-soccer', 'head-soccer-2-player'],
    'football-killer': ['football-brawl', 'football-blitz', 'football-strike', 'football-stars', 'football-legends'],
    'football-strike': ['football-brawl', 'football-blitz', 'football-killer', 'free-kick-football', 'soccer-free-kick'],
    'head-soccer': ['head-soccer-2-player', 'soccer-heads', 'super-soccer-noggins-xmas-edition', 'fiveheads-soccer', 'bobblehead-soccer'],
    'head-soccer-2-player': ['head-soccer', 'soccer-heads', 'super-soccer-noggins-xmas-edition', 'fiveheads-soccer', 'bobblehead-soccer'],
    'soccer-heads': ['head-soccer', 'head-soccer-2-player', 'super-soccer-noggins-xmas-edition', 'fiveheads-soccer', 'bobblehead-soccer'],
    'super-soccer-noggins-xmas-edition': ['head-soccer', 'head-soccer-2-player', 'soccer-heads', 'fiveheads-soccer', 'bobblehead-soccer'],
    'fiveheads-soccer': ['head-soccer', 'head-soccer-2-player', 'soccer-heads', 'super-soccer-noggins-xmas-edition', 'bobblehead-soccer'],
    'bobblehead-soccer': ['head-soccer', 'head-soccer-2-player', 'soccer-heads', 'super-soccer-noggins-xmas-edition', 'fiveheads-soccer'],

    // Football / Soccer Skills
    'soccer-skills': ['soccer-skills-euro', 'soccer-skills-world-cup', 'soccer-skills-champions-league', 'football-run', 'real-football'],
    'soccer-skills-euro': ['soccer-skills', 'soccer-skills-world-cup', 'soccer-skills-champions-league', 'football-run', 'real-football'],
    'soccer-skills-world-cup': ['soccer-skills', 'soccer-skills-euro', 'soccer-skills-champions-league', 'football-run', 'real-football'],
    'soccer-skills-champions-league': ['soccer-skills', 'soccer-skills-euro', 'soccer-skills-world-cup', 'football-run', 'real-football'],
    'football-run': ['soccer-skills', 'soccer-skills-euro', 'soccer-skills-world-cup', 'soccer-skills-champions-league', 'slope-two-players-multiplayer'],
    'real-football': ['soccer-skills', 'soccer-skills-euro', 'soccer-skills-world-cup', 'soccer-skills-champions-league', 'football-masters'],

    // Free Kicks / Penalty Games
    'football-penalty-champions': ['free-kick-football', 'soccer-free-kick', 'penalty-kick', 'penalty-shooters', 'penalty-shootout-multi-league'],
    'free-kick-football': ['football-penalty-champions', 'soccer-free-kick', 'penalty-kick', '3d-free-kick', 'football-strike'],
    'soccer-free-kick': ['football-penalty-champions', 'free-kick-football', 'penalty-kick', '3d-free-kick', 'football-strike'],
    'penalty-kick': ['football-penalty-champions', 'free-kick-football', 'soccer-free-kick', 'penalty-shooters', 'penalty-shootout-multi-league'],
    'penalty-shooters': ['football-penalty-champions', 'free-kick-football', 'soccer-free-kick', 'penalty-kick', 'penalty-shootout-multi-league'],
    'penalty-shootout-multi-league': ['football-penalty-champions', 'free-kick-football', 'soccer-free-kick', 'penalty-kick', 'penalty-shooters'],
    'penalty-challenge-multiplayer': ['football-penalty-champions', 'penalty-kick', 'penalty-shooters', 'penalty-shootout-multi-league', '3d-free-kick'],
    '3d-free-kick': ['football-penalty-champions', 'free-kick-football', 'soccer-free-kick', 'penalty-kick', 'penalty-shooters'],
    'hero-2-super-kick': ['football-killer', 'football-strike', '3d-free-kick', 'penalty-kick', 'soccer-free-kick'],

    // Soccer Variations / Multiplayer
    'soccer-random': ['pill-soccer', 'bumper-cars-soccer', '1v1-soccer', 'infinite-soccer', 'gravity-soccer'],
    'pill-soccer': ['soccer-random', 'bumper-cars-soccer', '1v1-soccer', 'infinite-soccer', 'gravity-soccer'],
    'bumper-cars-soccer': ['soccer-random', 'pill-soccer', '1v1-soccer', 'rocket-soccer-derby', 'infinite-soccer'],
    '1v1-soccer': ['soccer-random', 'pill-soccer', 'bumper-cars-soccer', 'infinite-soccer', 'gravity-soccer'],
    'infinite-soccer': ['soccer-random', 'pill-soccer', 'bumper-cars-soccer', '1v1-soccer', 'gravity-soccer'],
    'gravity-soccer': ['soccer-random', 'pill-soccer', '1v1-soccer', 'infinite-soccer', 'ball-sort-soccer'],
    'ball-sort-soccer': ['gravity-soccer', 'soccer-random', 'pill-soccer', 'infinite-soccer', 'soccer-free-kick'],
    'slope-two-players-multiplayer': ['slope-2-multiplayer', '2-player-tag', 'head-soccer-2-player', '1v1-soccer', 'soccer-random'],
    'slope-2-multiplayer': ['slope-two-players-multiplayer', '2-player-tag', 'head-soccer-2-player', '1v1-soccer', 'soccer-random'],
    'super-liquid-soccer': ['soccer-bros', 'soccer-legends', 'football-legends', 'football-masters', 'rocket-soccer-derby'],
    'heads-arena-euro-soccer': ['heads-arena-soccer-all-stars', 'head-soccer', 'head-soccer-2-player', 'soccer-heads', 'fiveheads-soccer'],
    'heads-arena-soccer-all-stars': ['heads-arena-euro-soccer', 'head-soccer', 'head-soccer-2-player', 'soccer-heads', 'fiveheads-soccer'],
    'soccer-bros': ['football-legends-2', 'football-legends', 'soccer-legends', 'super-liquid-soccer', 'rocket-soccer-derby'],
    'rocket-soccer-derby': ['bumper-cars-soccer', 'super-liquid-soccer', 'soccer-bros', 'soccer-legends', 'football-masters'],

    // Football Management / Trivia / Simulation
    'axis-football-league': ['real-football', 'retro-bowl-college-football', 'football-legends', 'football-masters', '4th-and-goal-2022', 'football-bros'],
    'immaculate-grid-football': ['axis-football-league', 'retro-bowl-college-football', 'real-football', 'football-legends', 'football-masters', 'football-bros'],

    // Racing / Driving
    'sports-bike-racing': ['super-bike-the-champion', 'super-mx-the-champion', '3d-moto-simulator-2', 'furious-racing-3d', 'rally-champion'],
    'super-bike-the-champion': ['sports-bike-racing', 'super-mx-the-champion', '3d-moto-simulator-2', 'furious-racing-3d', 'rally-champion'],
    'super-mx-the-champion': ['sports-bike-racing', 'super-bike-the-champion', '3d-moto-simulator-2', 'furious-racing-3d', 'rally-champion'],
    '3d-moto-simulator-2': ['sports-bike-racing', 'super-bike-the-champion', 'super-mx-the-champion', 'furious-racing-3d', 'rally-champion'],
    'furious-racing-3d': ['3d-car-simulator', 'rally-champion', 'car-parking-multiplayer', 'sports-bike-racing', '3d-moto-simulator-2'],
    'rally-champion': ['furious-racing-3d', '3d-car-simulator', 'car-parking-multiplayer', 'sports-bike-racing', 'super-bike-the-champion'],
    '3d-car-simulator': ['furious-racing-3d', 'rally-champion', 'car-parking-multiplayer', 'sports-bike-racing', '3d-moto-simulator-2'],
    'car-parking-multiplayer': ['3d-car-simulator', 'furious-racing-3d', 'rally-champion', 'sports-bike-racing', 'super-bike-the-champion'],

    // Hockey / Golf / Volleyball / Cricket
    'air-hockey-championship-deluxe': ['hockey-shootout', 'battle-golf', 'golf-champions', 'volleyball-challenge', 'cricket-hero'],
    'hockey-shootout': ['air-hockey-championship-deluxe', 'battle-golf', 'golf-champions', 'volleyball-challenge', 'cricket-hero'],
    'golf-champions': ['battle-golf', 'air-hockey-championship-deluxe', 'hockey-shootout', 'volleyball-challenge', 'cricket-hero'],
    'battle-golf': ['golf-champions', 'air-hockey-championship-deluxe', 'hockey-shootout', 'volleyball-challenge', 'cricket-hero'],
    'volleyball-challenge': ['air-hockey-championship-deluxe', 'hockey-shootout', 'golf-champions', 'battle-golf', 'cricket-hero'],
    'cricket-hero': ['cricket-fielder-challenge-game', 'champion-island', 'golf-champions', 'volleyball-challenge', 'rugby-rush'],
    'cricket-fielder-challenge-game': ['cricket-hero', 'champion-island', 'golf-champions', 'volleyball-challenge', 'rugby-rush'],
    'champion-island': ['cricket-hero', 'cricket-fielder-challenge-game', 'golf-champions', 'volleyball-challenge', 'battle-golf'],

    // Basketball / Cannon Basketball
    'cannon-basketball-2': ['cannon-basketball-3', 'cannon-basketball-4', 'basketball-serial-shooter', 'battle-golf', 'gravity-soccer'],
    'cannon-basketball-3': ['cannon-basketball-2', 'cannon-basketball-4', 'basketball-serial-shooter', 'battle-golf', 'gravity-soccer'],
    'cannon-basketball-4': ['cannon-basketball-2', 'cannon-basketball-3', 'basketball-serial-shooter', 'battle-golf', 'gravity-soccer'],
    'basketball-serial-shooter': ['cannon-basketball-2', 'cannon-basketball-3', 'cannon-basketball-4', 'battle-golf', 'gravity-soccer'],

    // Boxing / Fighting
    'stickman-boxing-ko-champion': ['drunken-boxing', 'drunken-boxing-2', 'boxing-phsyics-2', 'stick-duel-battle', 'stickman-fighter-epic-battle-2'],
    'drunken-boxing': ['drunken-boxing-2', 'stickman-boxing-ko-champion', 'boxing-phsyics-2', 'stick-duel-battle', 'stickman-fighter-epic-battle-2'],
    'drunken-boxing-2': ['drunken-boxing', 'stickman-boxing-ko-champion', 'boxing-phsyics-2', 'stick-duel-battle', 'stickman-fighter-epic-battle-2'],
    'boxing-phsyics-2': ['drunken-boxing', 'drunken-boxing-2', 'stickman-boxing-ko-champion', 'stick-duel-battle', 'stickman-fighter-epic-battle-2'],
    'stick-duel-battle': ['stickman-boxing-ko-champion', 'drunken-boxing', 'drunken-boxing-2', 'boxing-phsyics-2', 'stickman-fighter-epic-battle-2'],
    'stickman-fighter-epic-battle-2': ['stick-duel-battle', 'stickman-boxing-ko-champion', 'drunken-boxing', 'drunken-boxing-2', 'stickman-army-team-battle'],
    'stickman-army-team-battle': ['stick-duel-battle', 'stickman-boxing-ko-champion', 'stickman-fighter-epic-battle-2', 'funny-battle-simulator', 'squid-battle-simulator'],

    // Battle / Strategy / Simulation
    'funny-battle-simulator': ['funny-battle-simulator-2', 'squid-battle-simulator', 'ages-of-conflict', 'stickman-army-team-battle', 'battle-royale-noob-vs-pro'],
    'funny-battle-simulator-2': ['funny-battle-simulator', 'squid-battle-simulator', 'ages-of-conflict', 'stickman-army-team-battle', 'battle-royale-noob-vs-pro'],
    'squid-battle-simulator': ['funny-battle-simulator', 'funny-battle-simulator-2', 'ages-of-conflict', 'stickman-army-team-battle', 'battle-royale-noob-vs-pro'],
    'ages-of-conflict': ['funny-battle-simulator', 'funny-battle-simulator-2', 'squid-battle-simulator', 'stickman-army-team-battle', 'battle-royale-noob-vs-pro'],
    'battle-royale-noob-vs-pro': ['funny-battle-simulator', 'funny-battle-simulator-2', 'squid-battle-simulator', 'ages-of-conflict', 'stickman-fighter-epic-battle-2'],
    'snow-battle-io': ['battle-royale-noob-vs-pro', 'stick-duel-battle', 'squid-battle-simulator', 'funny-battle-simulator', 'ages-of-conflict'],
    'sniper-gun-shooting': ['battle-royale-noob-vs-pro', 'stick-duel-battle', 'squid-battle-simulator', 'funny-battle-simulator', 'ages-of-conflict'],

    // Multiplayer / Party Games
    'uno-4-colors-multiplayer': ['ludo-multiplayer', '2-player-tag', 'slope-2-multiplayer', 'slope-two-players-multiplayer', 'snow-battle-io'],
    'ludo-multiplayer': ['uno-4-colors-multiplayer', '2-player-tag', 'slope-2-multiplayer', 'slope-two-players-multiplayer', 'champion-island'],
    '2-player-tag': ['slope-2-multiplayer', 'slope-two-players-multiplayer', 'uno-4-colors-multiplayer', 'ludo-multiplayer', 'head-soccer-2-player'],

    // Endless Running / Platform / Skill
    'crowd-run-3d': ['death-run-3d', 'bottle-jump-3d', 'slope-two-players-multiplayer', 'football-run', 'duck-life-2-world-champion'],
    'death-run-3d': ['crowd-run-3d', 'bottle-jump-3d', 'slope-two-players-multiplayer', 'football-run', 'slope-2-multiplayer'],
    'bottle-jump-3d': ['crowd-run-3d', 'death-run-3d', 'football-run', 'slope-two-players-multiplayer', 'duck-life-2-world-champion'],

    // Duck Life / Sports Adventure
    'duck-life-2-world-champion': ['duck-life-2-world-champion', 'champion-island', 'football-run', 'crowd-run-3d', 'death-run-3d'],

    // Animal / 3D Simulator
    'dog-simulator-3d': ['fox-simulator-3d', 'horse-simulator-3d', 'panda-simulator-3d', 'tiger-simulator-3d', 'raccoon-adventure-city-simulator-3d'],
    'fox-simulator-3d': ['dog-simulator-3d', 'horse-simulator-3d', 'panda-simulator-3d', 'tiger-simulator-3d', 'raccoon-adventure-city-simulator-3d'],
    'horse-simulator-3d': ['dog-simulator-3d', 'fox-simulator-3d', 'panda-simulator-3d', 'tiger-simulator-3d', 'raccoon-adventure-city-simulator-3d'],
    'panda-simulator-3d': ['dog-simulator-3d', 'fox-simulator-3d', 'horse-simulator-3d', 'tiger-simulator-3d', 'raccoon-adventure-city-simulator-3d'],
    'tiger-simulator-3d': ['dog-simulator-3d', 'fox-simulator-3d', 'horse-simulator-3d', 'panda-simulator-3d', 'raccoon-adventure-city-simulator-3d'],
    'raccoon-adventure-city-simulator-3d': ['dog-simulator-3d', 'fox-simulator-3d', 'horse-simulator-3d', 'panda-simulator-3d', 'tiger-simulator-3d'],

    // Other Games (standalone but linked lightly)
    '1v1-soccer': ['soccer-random', 'pill-soccer', 'bumper-cars-soccer', 'soccer-bros', 'head-soccer'],
    'football-penalty-champions': ['free-kick-football', 'soccer-free-kick', 'penalty-kick', 'penalty-shooters', 'penalty-shootout-multi-league'],
    'immaculate-grid-football': ['axis-football-league', 'retro-bowl-college-football', 'real-football', 'football-legends', 'football-masters'],
    'uno-4-colors-multiplayer': ['ludo-multiplayer', '2-player-tag', 'champion-island', 'snow-battle-io', 'slope-2-multiplayer'],
    '1010-color-match': ['ball-sort-soccer', 'gravity-soccer', 'bottle-jump-3d', 'uno-4-colors-multiplayer', 'ludo-multiplayer']
  };

  return seriesMap[slug1]?.includes(slug2) ?? false;
}

/**
 * Enhanced game recommendations with series/version priority
 * Priority: Featured games > Series games > Same category games > Latest games
 */
export function useGameRecommendations(game: Game | null): GameRecommendations {
  return useMemo(() => {
    if (!game) return { sidebarGames: [], moreGames: [] };

    // Define priority games for homepage sidebar based on new categories
    const priorityGames = [
      'football-legends-2',
      'football-legends',
      'football-masters',
      'football-stars',
      'soccer-bros',
      'soccer-legends',
      'head-soccer',
      'head-soccer-2-player',
      'soccer-heads',
      'super-soccer-noggins-xmas-edition',
      'fiveheads-soccer',
      'bobblehead-soccer'
    ];
    // Get all games from all categories of current game
    const allCategories = game.categories || [];
    const allRelatedGames: Game[] = [];
    const usedSlugs = new Set<string>([game.slug]);

    // Collect games from all categories (not just primary)
    for (const category of allCategories) {
      const categoryGames = getGamesByCategory(category).filter((g) => !usedSlugs.has(g.slug));
      allRelatedGames.push(...categoryGames);
      categoryGames.forEach(g => usedSlugs.add(g.slug));
    }

    // PRIORITY 1: Get series/version games first
    const seriesGames = getSeriesGames(game, allRelatedGames);

    // PRIORITY 2: Get priority games from the current game's categories
    const priorityRelatedGames = allRelatedGames.filter(g =>
      priorityGames.includes(g.slug) && !seriesGames.some(sg => sg.slug === g.slug)
    ).sort((a, b) => {
      const aIndex = priorityGames.indexOf(a.slug);
      const bIndex = priorityGames.indexOf(b.slug);
      return aIndex - bIndex;
    });

    // PRIORITY 3: Get remaining category games (excluding series and priority games)
    const remainingCategoryGames = allRelatedGames.filter(g =>
      !seriesGames.some(sg => sg.slug === g.slug) &&
      !priorityRelatedGames.some(pg => pg.slug === g.slug)
    );

    // PRIORITY 4: Get latest games as fallback
    const latestGames = getLatestGames(GAME_LIMITS.LATEST_GAMES_POOL)
      .filter((g) => g.slug !== game.slug && !usedSlugs.has(g.slug));

    // Build sidebar with priority order
    const sidebar: Game[] = [];
    const finalUsedSlugs = new Set<string>([game.slug]);

    // Add series games first (highest priority for related games)
    for (const seriesGame of seriesGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        sidebar.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add priority games from same categories
    for (const priorityGame of priorityRelatedGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        sidebar.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        sidebar.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (sidebar.length >= GAME_LIMITS.SIDEBAR_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        sidebar.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    // Build more games section
    const more: Game[] = [];

    // Add remaining series games that didn't fit in sidebar
    for (const seriesGame of seriesGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(seriesGame.slug)) {
        more.push(seriesGame);
        finalUsedSlugs.add(seriesGame.slug);
      }
    }

    // Add remaining priority games
    for (const priorityGame of priorityRelatedGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(priorityGame.slug)) {
        more.push(priorityGame);
        finalUsedSlugs.add(priorityGame.slug);
      }
    }

    // Add remaining category games
    for (const categoryGame of remainingCategoryGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(categoryGame.slug)) {
        more.push(categoryGame);
        finalUsedSlugs.add(categoryGame.slug);
      }
    }

    // Fill up with latest games if needed
    for (const latestGame of latestGames) {
      if (more.length >= GAME_LIMITS.MORE_GAMES) break;
      if (!finalUsedSlugs.has(latestGame.slug)) {
        more.push(latestGame);
        finalUsedSlugs.add(latestGame.slug);
      }
    }

    return { sidebarGames: sidebar, moreGames: more };
  }, [game]);
}
